const cache = new Map();
const swaps = new WeakMap();
const widths = [240, 320, 480, 640, 800, 1000, 1200, 1500, 1800, 2200, 2600];

export const limitedConnection = () => {
  const connection = navigator.connection;
  return Boolean(connection?.saveData || /(^|-)[23]g$/.test(connection?.effectiveType || ''));
};

export function imageSource(source, displayWidth, maximum = 1800) {
  if (!source) return '';
  try {
    const url = new URL(source, location.href);
    if (!url.pathname.includes('/cdn/shop/') && url.hostname !== 'cdn.shopify.com') return source;
    const density = Math.min(window.devicePixelRatio || 1, 2);
    const needed = Math.max(1, Math.ceil(displayWidth * density));
    const width = Math.min(maximum, widths.find(value => value >= needed) || maximum);
    const oldWidth = Number(url.searchParams.get('width'));
    const oldHeight = Number(url.searchParams.get('height'));
    if (oldWidth && oldHeight) url.searchParams.set('height', String(Math.round(width * oldHeight / oldWidth)));
    url.searchParams.set('width', String(width));
    return url.href;
  } catch { return source; }
}

export function renderWidth(image, container = image) {
  const rect = container.getBoundingClientRect();
  const aspect = Number(image.getAttribute('width')) / Number(image.getAttribute('height'));
  return Math.ceil(getComputedStyle(image).objectFit === 'cover' && aspect > 0
    ? Math.max(rect.width, rect.height * aspect) : rect.width);
}

export function prepareImage(source, { width, maximum = 1800, srcset = '', sizes = '', priority = 'low' } = {}) {
  const src = width && !srcset ? imageSource(source, width, maximum) : source;
  const key = [src, srcset, sizes].join('|');
  if (cache.has(key)) return cache.get(key);
  const image = new Image();
  image.decoding = 'async';
  image.fetchPriority = priority;
  const pending = new Promise((resolve, reject) => {
    image.onload = async () => {
      try { await image.decode?.(); } catch {}
      if (image.naturalWidth) resolve(image); else reject(new Error('Image unavailable'));
    };
    image.onerror = () => reject(new Error('Image unavailable'));
    if (sizes) image.sizes = sizes;
    if (srcset) image.srcset = srcset;
    image.src = src;
  });
  cache.set(key, pending);
  pending.catch(() => { if (cache.get(key) === pending) cache.delete(key); });
  if (cache.size > 16) cache.delete(cache.keys().next().value);
  return pending;
}

export function cancelSwap(image) {
  const state = swaps.get(image);
  if (!state) return;
  state.animation?.cancel();
  state.overlay?.remove();
  swaps.delete(image);
}

export async function swapImage(image, source, { width, maximum = 1600, fallback = '', signal, animate = true, alt } = {}) {
  cancelSwap(image);
  const state = {};
  swaps.set(image, state);
  image.dataset.imageState = 'loading';
  const started = performance.now();
  try {
    const desired = imageSource(source, width, maximum);
    if (image.complete && image.naturalWidth && image.currentSrc) {
      const currentUrl = new URL(image.currentSrc, location.href);
      const desiredUrl = new URL(desired, location.href);
      const currentWidth = Number(currentUrl.searchParams.get('width'));
      const desiredWidth = Number(desiredUrl.searchParams.get('width'));
      const ratio = url => Number(url.searchParams.get('height')) / Number(url.searchParams.get('width')) || 0;
      const sameRatio = Math.abs(ratio(currentUrl) - ratio(desiredUrl)) < .001;
      for (const url of [currentUrl, desiredUrl]) { url.searchParams.delete('width'); url.searchParams.delete('height'); }
      if (currentUrl.href === desiredUrl.href && sameRatio && currentWidth >= desiredWidth) {
        if (alt !== undefined) image.alt = alt;
        image.dataset.imageState = 'ready';
        image.dataset.imageWaitMs = '0';
        return true;
      }
    }
    let prepared;
    try { prepared = await prepareImage(source, { width, maximum, priority: 'auto' }); }
    catch (error) {
      if (!fallback || fallback === source) throw error;
      prepared = await prepareImage(fallback, { width, maximum, priority: 'auto' });
    }
    if (signal?.aborted || swaps.get(image) !== state || !image.isConnected) return false;
    const next = prepared.currentSrc || prepared.src;
    if (image.currentSrc !== next && image.complete && image.naturalWidth && animate && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      state.overlay = image.cloneNode(false);
      state.overlay.removeAttribute('id');
      state.overlay.removeAttribute('data-vgm-product-modal-image');
      state.overlay.alt = '';
      state.overlay.setAttribute('aria-hidden', 'true');
      state.overlay.style.cssText = 'position:absolute;inset:0;inline-size:100%;block-size:100%;pointer-events:none;z-index:1';
      image.parentElement.append(state.overlay);
      state.animation = state.overlay.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 260, easing: 'cubic-bezier(.16,1,.3,1)' });
      state.animation.finished.catch(() => {}).finally(() => state.overlay?.remove());
    }
    image.removeAttribute('srcset');
    image.src = next;
    if (alt !== undefined) image.alt = alt;
    image.dataset.imageState = 'ready';
    image.dataset.imageWaitMs = String(Math.round(performance.now() - started));
    return true;
  } catch {
    if (swaps.get(image) === state) image.dataset.imageState = 'error';
    return false;
  }
}

const observed = new Set();
const resize = new ResizeObserver(entries => {
  for (const { target } of entries) {
    if (!target.isConnected) { resize.unobserve(target); observed.delete(target); continue; }
    const width = renderWidth(target);
    if (width > 0 && target.srcset) {
      const sizes = `${width}px`;
      if (target.sizes !== sizes) target.sizes = sizes;
    }
  }
});

const features = Array.from(document.querySelectorAll('[data-vgm-feature-modules]')).flatMap(node => {
  try { return JSON.parse(node.textContent); } catch { return []; }
});
const loaded = new Set();
function enhance(root) {
  if (!(root instanceof Element) && root !== document) return;
  const images = Array.from(root.querySelectorAll('img[data-vgm-responsive-image]'));
  if (root.matches?.('img[data-vgm-responsive-image]')) images.unshift(root);
  for (const image of images) {
    if (observed.has(image)) continue;
    observed.add(image);
    const width = renderWidth(image);
    if (width && image.srcset) image.sizes = `${width}px`;
    resize.observe(image);
  }
  for (const feature of features) {
    if (loaded.has(feature.url) || !(root.matches?.(feature.selector) || root.querySelector(feature.selector))) continue;
    loaded.add(feature.url);
    import(feature.url).catch(() => loaded.delete(feature.url));
  }
}
enhance(document);
const mutations = new MutationObserver(records => {
  for (const record of records) for (const node of record.addedNodes) enhance(node);
  for (const image of observed) if (!image.isConnected) { resize.unobserve(image); observed.delete(image); }
});
mutations.observe(document.body, { childList: true, subtree: true });
window.VGMImages = { imageSource, prepareImage, swapImage, cancelSwap, limitedConnection, renderWidth };
